import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, Crown, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { settingsAPI } from '../services/api';

export default function AccessBanner({ user }) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [whatsappSettings, setWhatsappSettings] = useState(null);

  // Fetch WhatsApp settings
  useEffect(() => {
    const fetchWhatsAppSettings = async () => {
      try {
        const data = await settingsAPI.getPublicSettings();
        setWhatsappSettings(data);
      } catch (error) {
        console.error('Failed to load WhatsApp settings:', error);
      }
    };

    fetchWhatsAppSettings();
  }, []);

  useEffect(() => {
    if (!user || user.role === 'admin' || !user.access_expired_date) {
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiry = new Date(user.access_expired_date);
      const diff = expiry - now;

      if (diff <= 0) {
        return { expired: true };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return { expired: false, days, hours, minutes };
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update every minute
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);

  const handleWhatsAppUpgrade = () => {
    if (whatsappSettings?.whatsapp_number) {
      const phone = whatsappSettings.whatsapp_number.replace(/[^0-9]/g, '');
      const userName = user?.name || user?.email || 'User';
      const message = encodeURIComponent(
        `Hello Admin, I'm ${userName} and I would like to upgrade my trial account to a Premium plan. Please assist me with the upgrade process. Thank you!`
      );
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    }
  };

  if (!user || user.role === 'admin' || !user.access_expired_date || !timeRemaining) {
    return null;
  }

  if (timeRemaining.expired) {
    return null; // Will be redirected by route guard
  }

  const formatExpiryDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Trial banner (orange/yellow)
  if (user.role === 'trial') {
    return (
      <>
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-500 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-900">
                  Your trial period:{' '}
                  <span className="font-bold">
                    {timeRemaining.days} Day{timeRemaining.days !== 1 ? 's' : ''} {timeRemaining.hours} Hour{timeRemaining.hours !== 1 ? 's' : ''}
                  </span>
                  {' '}remaining
                </p>
                <p className="text-xs text-orange-700 mt-0.5">
                  Expires on {formatExpiryDate(user.access_expired_date)}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => setShowUpgradeDialog(true)}
            >
              Upgrade to Premium
            </Button>
          </div>
        </div>

        {/* Upgrade Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent className="sm:max-w-lg">
            {/* Header with gradient background */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-orange-500 to-amber-600 rounded-t-lg" />
            
            <DialogHeader className="relative">
              {/* Crown Icon for Premium Upgrade */}
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg ring-8 ring-orange-50">
                <Crown className="h-10 w-10 text-orange-600" />
              </div>
              
              <DialogTitle className="text-center text-2xl font-bold text-gray-900">
                Upgrade to Premium Plan
              </DialogTitle>
              
              <DialogDescription className="text-center space-y-4 pt-4">
                <p className="text-base text-gray-700">
                  Unlock full access to <span className="font-semibold text-orange-600">NUTRIMUS</span> with extended usage period!
                </p>
                
                {/* Benefits Box */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left">
                  <p className="text-sm font-semibold text-orange-900 mb-2">
                    ✨ Premium Plan Benefits:
                  </p>
                  <ul className="text-sm text-orange-800 space-y-1.5 ml-4">
                    <li>• Masa aktif sesuai perpanjangan</li>
                    <li>• Kapasitas hingga 60 pasien</li>
                    <li>• Perhitungan & recall tanpa batas (Unlimited)</li>
                    <li>• Prioritas dukungan admin</li>
                  </ul>
                </div>

                {/* Contact Admin Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-gray-800 mb-2">
                    📞 Contact Administrator
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    Get in touch with admin to discuss upgrade options and activate your Premium plan.
                  </p>

                  {/* WhatsApp CTA */}
                  {whatsappSettings?.whatsapp_number ? (
                    <>
                      <Button
                        onClick={handleWhatsAppUpgrade}
                        className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-md mb-2"
                        size="lg"
                      >
                        <MessageCircle className="h-5 w-5 mr-2" />
                        Contact Admin via WhatsApp
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        {whatsappSettings.whatsapp_number}
                      </p>
                    </>
                  ) : (
                    <div className="text-sm text-gray-600 p-3 bg-amber-50 rounded border border-amber-200">
                      Please contact your administrator to upgrade your account.
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter className="sm:justify-center mt-6">
              <Button
                onClick={() => setShowUpgradeDialog(false)}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Premium banner (only shown when access period is <= 10 days)
  if (user.role === 'premium') {
    // Sembunyikan notifikasi jika masa aktif masih lebih dari 10 hari
    if (timeRemaining.days > 10) {
      return null;
    }

    const isUrgent = timeRemaining.days <= 3;
    const bgColor = isUrgent ? 'from-red-50 to-rose-50' : 'from-amber-50 to-orange-50';
    const borderColor = isUrgent ? 'border-red-500' : 'border-amber-500';
    const textColor = isUrgent ? 'text-red-900' : 'text-amber-900';
    const iconColor = isUrgent ? 'text-red-600' : 'text-amber-600';
    const subtextColor = isUrgent ? 'text-red-700' : 'text-amber-700';

    return (
      <div className={`bg-gradient-to-r ${bgColor} border-l-4 ${borderColor} px-4 py-3 shadow-sm`}>
        <div className="flex items-center gap-3">
          <Clock className={`h-5 w-5 ${iconColor} flex-shrink-0`} />
          <div>
            <p className={`text-sm font-medium ${textColor}`}>
              Remaining Access Period:{' '}
              <span className="font-bold">
                {timeRemaining.days} Day{timeRemaining.days !== 1 ? 's' : ''} {timeRemaining.hours} Hour{timeRemaining.hours !== 1 ? 's' : ''}
              </span>
              {' '}(Expires on {formatExpiryDate(user.access_expired_date)})
            </p>
            <p className={`text-xs ${subtextColor} mt-0.5`}>
              ⚠️ Masa aktif akun Premium Anda tersisa {timeRemaining.days} hari lagi. Hubungi admin untuk perpanjangan masa akses.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
